import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBrandManager } from "@/hooks/useBrandManager";

interface GrowthSquadMember {
  id: string;
  name: string;
  alias: string;
  email: string;
  user_id: string;
  avatar_url?: string;
}

export const useGrowthSquadMembers = () => {
  const { currentBrandId } = useBrandManager();

  const { data: members = [], isLoading, refetch } = useQuery({
    queryKey: ['growth-squad-members', currentBrandId],
    retry: false,
    queryFn: async (): Promise<GrowthSquadMember[]> => {
      if (!currentBrandId) return [];

      const { data, error } = await supabase
        .from("growth_squad")
        .select("id, name, alias, email, user_id")
        .eq("brand_id", currentBrandId);

      if (error) throw error;

      const userIds = (data || []).filter(m => m.user_id).map(m => m.user_id);
      let avatarMap: Record<string, string> = {};

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, avatar_url")
          .in("id", userIds);

        if (profiles) {
          const supabaseUrl = (supabase as any).supabaseUrl || 'https://hjodtxoylcvcwjgvuctk.supabase.co';
          avatarMap = profiles.reduce((acc, p) => {
            if (p.avatar_url) {
              acc[p.id] = p.avatar_url;
            } else {
              acc[p.id] = `${supabaseUrl}/storage/v1/object/public/avatars/${p.id}/avatar.png`;
            }
            return acc;
          }, {} as Record<string, string>);
        }
      }

      return (data || []).map((member: any) => ({
        ...member,
        avatar_url: member.user_id ? avatarMap[member.user_id] || null : null,
      }));
    },
    enabled: !!currentBrandId,
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    members,
    isLoading,
    refetch,
  };
};
